"use client";

import type { AssistantStaticBubbleVariantProps } from "@ui/components/core/BaseChat";
import { assistantBubbleBackgroundVariants, assistantBubbleRootVariants } from "@ui/components/core/BaseChat";
import * as m from "motion/react-m";
import { useEffect, useMemo, useRef } from "react";
import { AssistantToolPart } from "./toolCallParts";
import type { JSX, PropsWithChildren } from "react";
import type { ChatMessagePart, ChatPartCompleteProps } from "../types";
import { noop } from "../utils";

const SPEECH_WORD_DELAY_MS = 120;
const SPEECH_WORD_DELAY_SECONDS = SPEECH_WORD_DELAY_MS / 1000;
const defaultAssistantMessageVariants: AssistantStaticBubbleVariantProps = { variant: "cream" };

function AssistantBubbleFrame({
  children,
  assistantMessageVariants = defaultAssistantMessageVariants,
}: PropsWithChildren<{
  assistantMessageVariants?: AssistantStaticBubbleVariantProps;
}>): JSX.Element {
  return (
    <div className={assistantBubbleRootVariants(assistantMessageVariants)}>
      <div className={assistantBubbleBackgroundVariants(assistantMessageVariants)} />
      {children}
    </div>
  );
}

export function AssistantTextBubble({
  content,
  assistantMessageVariants = defaultAssistantMessageVariants,
}: {
  content: string;
  assistantMessageVariants?: AssistantStaticBubbleVariantProps;
}): JSX.Element {
  return (
    <AssistantBubbleFrame assistantMessageVariants={assistantMessageVariants}>
      <span>{content}</span>
    </AssistantBubbleFrame>
  );
}

function StaticTextMessagePart({
  content,
  onComplete,
  assistantMessageVariants = defaultAssistantMessageVariants,
}: {
  content: string;
  onComplete: () => void;
  assistantMessageVariants?: AssistantStaticBubbleVariantProps;
}): JSX.Element {
  useEffect(() => {
    const frameId = window.requestAnimationFrame(onComplete);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [onComplete]);

  return <AssistantTextBubble content={content} assistantMessageVariants={assistantMessageVariants} />;
}

function SpeakingTextMessagePart({
  content,
  onComplete,
  assistantMessageVariants = defaultAssistantMessageVariants,
}: {
  content: string;
  onComplete: () => void;
  assistantMessageVariants?: AssistantStaticBubbleVariantProps;
}): JSX.Element {
  const words = useMemo(() => content.trim().split(/\s+/).filter(Boolean), [content]);
  const hasCompletedRef = useRef(false);

  useEffect(() => {
    hasCompletedRef.current = false;

    if (words.length === 0) {
      onComplete();
      hasCompletedRef.current = true;
    }
  }, [onComplete, words]);

  return (
    <AssistantBubbleFrame assistantMessageVariants={assistantMessageVariants}>
      {words.map((word, index) => (
        <m.span
          key={index}
          initial={{ display: "none" }}
          animate={{ display: "inline" }}
          transition={{ duration: 0, delay: (index + 1) * SPEECH_WORD_DELAY_SECONDS }}
          onAnimationComplete={() => {
            if (index === words.length - 1 && !hasCompletedRef.current) {
              hasCompletedRef.current = true;
              onComplete();
            }
          }}
        >
          {word}
          {index < words.length - 1 ? " " : null}
        </m.span>
      ))}
    </AssistantBubbleFrame>
  );
}

interface AssistantPartProps {
  part: ChatMessagePart;
  onComplete: ChatPartCompleteProps["onComplete"];
  assistantMessageVariants?: AssistantStaticBubbleVariantProps;
}

interface StaticAssistantPartProps {
  part: ChatMessagePart;
  assistantMessageVariants?: AssistantStaticBubbleVariantProps;
}

export function StaticAssistantPart({
  part,
  assistantMessageVariants = defaultAssistantMessageVariants,
}: StaticAssistantPartProps): JSX.Element | null {
  switch (part.type) {
    case "text":
    case "speech":
      return <AssistantTextBubble content={part.content} assistantMessageVariants={assistantMessageVariants} />;
    case "tool":
      return (
        <AssistantToolPart
          part={part}
          onComplete={noop}
          animate={false}
          assistantMessageVariants={assistantMessageVariants}
        />
      );
    case "custom": {
      const CustomPart = part.render;

      return <CustomPart onComplete={noop} />;
    }
    default:
      return null;
  }
}

export function AssistantPart({
  part,
  onComplete,
  assistantMessageVariants = defaultAssistantMessageVariants,
}: AssistantPartProps): JSX.Element | null {
  switch (part.type) {
    case "text":
      return (
        <StaticTextMessagePart
          content={part.content}
          onComplete={onComplete}
          assistantMessageVariants={assistantMessageVariants}
        />
      );
    case "speech":
      return (
        <SpeakingTextMessagePart
          content={part.content}
          onComplete={onComplete}
          assistantMessageVariants={assistantMessageVariants}
        />
      );
    case "tool":
      return (
        <AssistantToolPart part={part} onComplete={onComplete} assistantMessageVariants={assistantMessageVariants} />
      );
    case "custom": {
      const CustomPart = part.render;

      return <CustomPart onComplete={onComplete} />;
    }
    default:
      return null;
  }
}
